import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../store/toast.store';
import * as service from './service';
import type { DownloadOnlyTaskUserDTO } from './types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  'admin role required': '无管理员权限',
  'admin permission required': '无操作权限',
  network_error: '网络连接失败，请检查网络',
};

function resolveError(error: unknown): string {
  if (isApiError(error)) return ERROR_MAP[error.message] ?? `请求失败 (${error.message})`;
  return '未知错误';
}

export function useDownloadOnlyTaskUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToastStore((s) => s.addToast);

  const userId = searchParams.get('userId') || '';
  const keyword = searchParams.get('keyword') || '';
  const accountKeyword = searchParams.get('accountKeyword') || '';
  const nameKeyword = searchParams.get('nameKeyword') || '';
  const targetHash = searchParams.get('targetHash') || '';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;

  const [items, setItems] = useState<DownloadOnlyTaskUserDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingUserIds, setUpdatingUserIds] = useState<number[]>([]);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await service.listDownloadOnlyTaskUsers({
        page,
        pageSize,
        userId,
        keyword,
        accountKeyword,
        nameKeyword,
        targetHash,
      });
      setItems(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [accountKeyword, keyword, nameKeyword, page, pageSize, targetHash, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = useCallback((name: string, value: string) => {
    updateParams({ [name]: value.trim() || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const refreshList = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await service.refreshDownloadOnlyTaskUsers();
      addToast('success', `名单已刷新，共 ${result.refreshed_count ?? 0} 个用户`);
      await load();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setRefreshing(false);
    }
  }, [addToast, load]);

  const updateShowNormalTasks = useCallback(async (userId: number, showNormalTasks: boolean) => {
    setUpdatingUserIds((prev) => Array.from(new Set([...prev, userId])));
    try {
      const updated = await service.updateDownloadOnlyTaskUserShowNormalTasks(userId, {
        show_normal_tasks: showNormalTasks,
      });
      setItems((prev) => prev.map((item) => {
        if (item.target_hash === updated.target_hash) {
          return { ...item, show_normal_tasks: item.user_id === updated.user_id };
        }
        return item.user_id === updated.user_id ? { ...item, ...updated } : item;
      }));
      addToast('success', '已设置展示普通任务账号');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setUpdatingUserIds((prev) => prev.filter((id) => id !== userId));
    }
  }, [addToast]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items,
    total,
    page,
    pageSize,
    totalPages,
    userId,
    keyword,
    accountKeyword,
    nameKeyword,
    targetHash,
    loading,
    error,
    refreshing,
    updatingUserIds,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload: load,
    refreshList,
    updateShowNormalTasks,
  };
}
